import { motion } from "motion/react";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button, Field, TextInput } from "@/components/ui";
import { consumeGoogleOutcome, googleStartUrl } from "@/lib/googleAuth";
import { useAuthConfig, useLogin } from "@/lib/queries";
import { spring } from "@/lib/motion";

export function Login() {
  const login = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const config = useAuthConfig();
  // back from Google without a session (no invitation, cancelled…): say why
  const [google, setGoogle] = useState(() => ({
    leaving: false,
    error: consumeGoogleOutcome()?.error ?? null,
  }));

  // back from Google's page with the browser's back button: the page comes from the cache
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (e.persisted) setGoogle((g) => ({ ...g, leaving: false }));
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ username, password });
  };

  return (
    <div className="grid min-h-dvh place-items-center px-5 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="ambient" aria-hidden />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={spring.gentle}
        className="w-full max-w-sm space-y-5 rounded-[28px] p-6 glass"
      >
        <div className="flex flex-col items-center gap-3 pb-2 text-center">
          <motion.img
            src="/icon.svg"
            alt=""
            className="size-16 rounded-[18px] shadow-lg"
            initial={{ rotate: -8, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ ...spring.gentle, delay: 0.1 }}
          />
          <div>
            <h1 className="text-title">SavingApp</h1>
            <p className="text-subhead text-muted">Las finanzas de casa, claras.</p>
          </div>
        </div>
        <Field label="Usuario">
          {(id) => (
            <TextInput
              id={id}
              autoComplete="username"
              autoCapitalize="none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Contraseña" error={login.error?.message}>
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" block loading={login.isPending}>
          Entrar
        </Button>
        {config.data?.google && (
          <>
            <div className="flex items-center gap-3 text-footnote text-muted" aria-hidden>
              <span className="h-px flex-1 bg-separator" />o
              <span className="h-px flex-1 bg-separator" />
            </div>
            <Button
              type="button"
              variant="secondary"
              block
              loading={google.leaving}
              onClick={() => {
                setGoogle({ leaving: true, error: null });
                window.location.assign(googleStartUrl());
              }}
            >
              Continuar con Google
            </Button>
            {google.error && (
              <p role="alert" className="text-center text-footnote text-negative">
                {google.error}
              </p>
            )}
          </>
        )}
        <div className="pt-2 text-center">
          <Link to="/privacidad" className="text-footnote text-muted underline hover:text-text">
            Aviso de privacidad
          </Link>
        </div>
      </motion.form>
    </div>
  );
}
